import axios from 'axios';
import { useQuery } from 'react-query';

const url = process.env.REACT_APP_API_URL + '/api/v1';

// Request throttling and deduplication
const pendingRequests = new Map();
const requestTimestamps = [];
const MAX_REQUESTS_PER_SECOND = 5;
const TIME_WINDOW = 1000; // 1 second

// Membuat instance axios dengan interceptor
const createInstance = () => {
  const instance = axios.create({
    baseURL: url,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  instance.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Rate limiting: prevent more than MAX_REQUESTS_PER_SECOND per second
      const now = Date.now();
      // Remove timestamps older than TIME_WINDOW
      while (
        requestTimestamps.length > 0 &&
        requestTimestamps[0] < now - TIME_WINDOW
      ) {
        requestTimestamps.shift();
      }

      if (requestTimestamps.length >= MAX_REQUESTS_PER_SECOND) {
        console.warn(
          `Rate limit exceeded: ${requestTimestamps.length} requests in the last second. Rejecting request.`,
        );
        return Promise.reject(
          new Error('Too many requests. Please slow down.'),
        );
      }

      requestTimestamps.push(now);

      // Request deduplication: prevent duplicate simultaneous requests
      const requestKey = `${config.method}:${config.url}:${JSON.stringify(
        config.params || {},
      )}:${JSON.stringify(config.data || {})}`;

      if (pendingRequests.has(requestKey)) {
        console.warn(
          `Duplicate request detected: ${requestKey}. Using existing request.`,
        );
        return pendingRequests.get(requestKey);
      }

      // Store the request promise
      const requestPromise = config;
      pendingRequests.set(requestKey, requestPromise);

      // Clean up after request completes
      const cleanup = () => pendingRequests.delete(requestKey);
      config.cleanup = cleanup;

      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  // Response interceptor to clean up pending requests
  instance.interceptors.response.use(
    (response) => {
      if (response.config.cleanup) {
        response.config.cleanup();
      }
      return response;
    },
    (error) => {
      if (error.config?.cleanup) {
        error.config.cleanup();
      }
      return Promise.reject(error);
    },
  );

  return instance;
};

// Queries
export const useAuthMe = () =>
  useQuery(
    ['authMe'],
    async () => {
      const instance = createInstance();
      const { data } = await instance.get('/auth/me');
      return data;
    },
    { enabled: false },
  );

export const useAuthApp = () =>
  useQuery(
    ['authApp'],
    async () => {
      const instance = createInstance();
      const { data } = await instance.get('/auth/app');
      return data;
    },
    { enabled: false },
  );

export const useApplications = () =>
  useQuery(
    ['applications'],
    async () => {
      const instance = createInstance();
      const { data } = await instance.get('/application');
      return data;
    },
    { enabled: false },
  );

export const useApplicationDetail = (code) =>
  useQuery(
    ['application', code],
    async () => {
      const instance = createInstance();
      const { data } = await instance.get(`/application/${code}`);
      return data;
    },
    {
      enabled: !!code, // Only fetch when code is provided
    },
  );

export const useUsers = (page = 1, limit = 10, username = '') => useQuery(['users', page, limit, username], async () => {
  const instance = createInstance();
  const params = {
    page: page,
    limit: limit
  };

  // Only add username filter if it's not empty
  if (username && username.trim()) {
    params.username = username.trim();
  }

  const { data } = await instance.get('/user', { params });
  return data;
}, { enabled: false, keepPreviousData: true });

export const useUserDetail = (username) =>
  useQuery(
    ['user', username],
    async () => {
      const instance = createInstance();
      const { data } = await instance.get(`/user/${username}`);
      return data;
    },
    {
      enabled: !!username, // Ensure query is only enabled when username is not null or undefined
    },
  );

export const useReferenceRoles = () =>
  useQuery(
    ['referenceRoles'],
    async () => {
      const instance = createInstance();
      const { data } = await instance.get('/reference/role');
      return data;
    },
    { enabled: false },
  );

export const useReferenceApps = () =>
  useQuery(
    ['referenceApps'],
    async () => {
      const instance = createInstance();
      const { data } = await instance.get('/reference/application');
      return data;
    },
    { enabled: false },
  );

export const useRoles = () =>
  useQuery(
    ['roles'],
    async () => {
      const instance = createInstance();
      const { data } = await instance.get('/role');
      return data;
    },
    { enabled: false },
  );

export const useRoleDetail = (code) =>
  useQuery(
    ['role', code],
    async () => {
      const instance = createInstance();
      const { data } = await instance.get(`/role/${code}`);
      return data;
    },
    { enabled: false, retry: false },
  );

export const usePermissions = () =>
  useQuery(
    ['permissions'],
    async () => {
      const instance = createInstance();
      const { data } = await instance.get('/permission');
      return data;
    },
    { enabled: false },
  );

export const useRedirectLink = (url) =>
  useQuery(
    ['redirectLink', url],
    async () => {
      const instance = createInstance();
      const { data } = await instance.get(
        `auth/app/redirect?destination_url=${url}`,
      );
      return data;
    },
    {
      enabled: !!url,
      staleTime: 0,
      cacheTime: 0,
    },
  );
